import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter, Link, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import NavBar from './components/NavBar/NavBar';
import { CaseStudyChromeProvider } from './components/CaseStudyMotion/CaseStudyChromeContext';
import { MeadowThemeProvider } from './hooks/MeadowThemeContext';
import { caseStudies } from './data/caseStudies';
import { scrollBehavior } from './utils/scrollBehavior';
import Home from './pages/Home/Home';
import './App.css';
import './styles/siteSystem.css';

// We decide scroll position on every route change, so stop the browser restoring an old one.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const loadProjectDetail = () => import('./pages/ProjectDetail/ProjectDetail');

// A tab left open across a deploy asks for a bundle that no longer exists. Reload once for the new build.
const loadWithRecovery = () => loadProjectDetail().then((page) => {
  sessionStorage.removeItem('reloaded-for-update');
  return page;
}).catch((error) => {
  if (sessionStorage.getItem('reloaded-for-update')) throw error;
  sessionStorage.setItem('reloaded-for-update', '1');
  window.location.reload();
  return new Promise(() => {});
});
const ProjectDetail = lazy(loadWithRecovery);

function ScrollToHash() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const anchor = /^#[\w-]+$/.test(hash) ? hash : ''; // Ignore settings like #mode=dark
  const positions = useRef({});
  const currentKey = useRef(key);
  currentKey.current = key;

  // Remember where each history entry was, so Back and Forward can return to it
  useEffect(() => {
    const remember = () => { positions.current[currentKey.current] = window.scrollY; };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, []);

  const restoring = navigationType === 'POP' && positions.current[key] != null;

  // Back or Forward: return to the saved position, retrying while the page grows to fit it.
  useLayoutEffect(() => {
    if (!restoring) return undefined;
    const target = positions.current[key];
    let frames = 0;
    let raf = 0;
    const restore = () => {
      window.scrollTo({ top: target, left: 0, behavior: 'instant' });
      frames += 1;
      if (Math.abs(window.scrollY - target) > 2 && frames < 60) raf = requestAnimationFrame(restore);
    };
    restore();
    return () => cancelAnimationFrame(raf);
  }, [key, restoring]);

  // New page without a hash: jump to the top before paint. Use 'instant' so the global
  // `scroll-behavior: smooth` doesn't turn this into a long animation that loading
  // images / the hero film interrupt partway down the page.
  useLayoutEffect(() => {
    if (!anchor && !restoring) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, anchor, restoring]);

  useEffect(() => {
    if (!anchor || restoring) return undefined;

    const id = anchor.replace('#', '');
    let attempts = 0;

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        return true;
      }
      return false;
    };

    if (scroll()) return undefined;

    const timer = window.setInterval(() => {
      attempts += 1;
      if (scroll() || attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 50);

    return () => window.clearInterval(timer);
  }, [pathname, anchor, restoring]);

  return null;
}

const SITE_TITLE = 'Nivedha Shanmugam | Product Designer';
const CASE_TITLES = { 'oracle-payments': 'Oracle Payments', 'qr-pay': 'Oracle QR Pay', kit: 'Kit', ditto: 'Ditto' };

/* Each route gets its own title, and focus moves to the new page for keyboard and screen reader users */
function RouteAnnouncer() {
  const { pathname, hash } = useLocation();
  const first = useRef(true);

  useEffect(() => {
    const slug = pathname.match(/^\/work\/([^/]+)/)?.[1];
    const known = pathname === '/' || (slug && CASE_TITLES[slug]);
    document.title = slug && CASE_TITLES[slug]
      ? `${CASE_TITLES[slug]} | Nivedha Shanmugam`
      : known ? SITE_TITLE : 'Page not found | Nivedha Shanmugam';

    if (first.current) {
      first.current = false;
      return undefined;
    }
    if (hash) return undefined;
    // The next page can mount late (lazy route), so keep trying until focus actually lands on it
    let tries = 0;
    const timer = setInterval(() => {
      document.querySelector('main')?.focus({ preventScroll: true });
      tries += 1;
      if (tries >= 8) clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, [pathname, hash]);

  return null;
}

function NotFound() {
  return (
    <main className="not-found" id="top" tabIndex={-1}>
      <h1>Page not found</h1>
      <p>This page doesn’t exist, but my work does.</p>
      <Link to="/">Back to home</Link>
    </main>
  );
}

function AppShell() {
  const location = useLocation();

  // Fetch the case study page once the home page is idle, so opening one feels instant
  useEffect(() => {
    const timer = setTimeout(() => loadProjectDetail().catch(() => {}), 2500);
    return () => clearTimeout(timer);
  }, []);

  const slug = location.pathname.match(/^\/work\/([^/]+)/)?.[1];
  const study = slug ? caseStudies[slug] : null;

  return (
    <MeadowThemeProvider>
      <CaseStudyChromeProvider study={study}>
        <ScrollToHash />
        <RouteAnnouncer />
        <a className="skip-link" href="#top">Skip to content</a>
        <NavBar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/work/:slug" element={<Suspense fallback={null}><ProjectDetail /></Suspense>} />
          <Route path="/work" element={<Navigate to="/#work" replace />} />
          <Route path="/about" element={<Navigate to="/#about" replace />} />
          <Route path="/resume" element={<Navigate to="/#resume" replace />} />
          <Route path="/contact" element={<Navigate to="/#contact" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </CaseStudyChromeProvider>
    </MeadowThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <AppShell />
        <Analytics />
      </div>
    </BrowserRouter>
  );
}

export default App;
